import React from 'react';
import { useHistory } from 'react-router-dom';
import { theme } from '../../styles/theme';

interface Quest {
    id: string;
    title: string;
    titleRu: string;
    description: string;
    progress: number;
    target: number;
    reward: { xp: number; coins: number };
    icon: string;
    completed: boolean;
}

const DAILY_QUESTS: Quest[] = [
    {
        id: '1',
        title: 'First Puzzle of the Day',
        titleRu: 'Первый пазл дня',
        description: 'Complete any puzzle today',
        progress: 0,
        target: 1,
        reward: { xp: 50, coins: 10 },
        icon: '🌅',
        completed: false
    },
    {
        id: '2',
        title: 'Russian Explorer',
        titleRu: 'Русский исследователь',
        description: 'Complete a Russian themed puzzle',
        progress: 0,
        target: 1,
        reward: { xp: 75, coins: 15 },
        icon: '🪆',
        completed: false
    },
    {
        id: '3',
        title: 'Challenge Accepted',
        titleRu: 'Вызов принят',
        description: 'Complete a hard or expert puzzle',
        progress: 0,
        target: 1,
        reward: { xp: 100, coins: 25 },
        icon: '🏆',
        completed: false
    },
];

const WEEKLY_QUESTS: Quest[] = [
    {
        id: '4',
        title: 'Puzzle Master',
        titleRu: 'Мастер пазлов',
        description: 'Complete 5 puzzles this week',
        progress: 2,
        target: 5,
        reward: { xp: 250, coins: 50 },
        icon: '⭐',
        completed: false
    },
    {
        id: '5',
        title: 'Category Explorer',
        titleRu: 'Исследователь категорий',
        description: 'Complete puzzles from 3 different categories',
        progress: 1,
        target: 3,
        reward: { xp: 150, coins: 30 },
        icon: '🗺️',
        completed: false
    },
];

const Quests: React.FC = () => {
    const history = useHistory();

    const renderQuest = (quest: Quest) => (
        <div
            key={quest.id}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px',
                backgroundColor: quest.completed ? theme.colors.cardBg : theme.colors.bgPrimary,
                borderRadius: '12px',
                border: `1px solid ${theme.colors.cardBorder}`,
                opacity: quest.completed ? 0.7 : 1,
            }}
        >
            <div style={{
                fontSize: '32px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.cardBg,
                borderRadius: '12px',
            }}>
                {quest.icon}
            </div>

            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: theme.colors.textPrimary, marginBottom: '4px' }}>
                    {quest.title}
                </div>
                <div style={{ fontSize: '13px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                    {quest.description}
                </div>

                {/* Progress bar */}
                <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: theme.colors.cardBorder,
                    borderRadius: '4px',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        width: `${(quest.progress / quest.target) * 100}%`,
                        height: '100%',
                        backgroundColor: quest.completed ? theme.colors.success : theme.colors.accent,
                        borderRadius: '4px',
                        transition: 'width 0.3s ease',
                    }} />
                </div>

                <div style={{
                    fontSize: '12px',
                    color: theme.colors.textSecondary,
                    marginTop: '4px',
                }}>
                    {quest.progress} / {quest.target}
                </div>
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
            }}>
                <span style={{ fontSize: '12px', color: theme.colors.accent }}>
                    +{quest.reward.xp} XP
                </span>
                <span style={{ fontSize: '12px', color: '#FFD700' }}>
                    🪙 {quest.reward.coins}
                </span>
            </div>
        </div>
    );

    return (
        <div style={{
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto',
            paddingBottom: '100px',
        }}>
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <h1 style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                }}>
                    Задания / Quests 🎯
                </h1>
                <p style={{ color: theme.colors.textSecondary }}>
                    Complete puzzles to earn rewards!
                </p>
            </div>

            {/* Daily Quests */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: theme.colors.textPrimary,
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    🌅 Daily Quests
                    <span style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                        fontWeight: 400,
                    }}>
                        Resets in 8h
                    </span>
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {DAILY_QUESTS.map(renderQuest)}
                </div>
            </div>

            {/* Weekly Quests */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: theme.colors.textPrimary,
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    📅 Weekly Quests
                    <span style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                        fontWeight: 400,
                    }}>
                        Resets in 3d
                    </span>
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {WEEKLY_QUESTS.map(renderQuest)}
                </div>
            </div>

            {/* CTA Button */}
            <button
                onClick={() => history.push('/')}
                style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: theme.colors.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: theme.shadows.md,
                }}
            >
                Start Playing 🧩
            </button>
        </div>
    );
};

export default Quests;
